import "./assets/reset.css";
import './assets/main-static.less';

import {createApp, markRaw} from 'vue';
import {createPinia} from 'pinia';
import * as Sentry from "@sentry/vue";
import {createSentryPiniaPlugin} from "@sentry/vue";

import App from './App.vue';
import router from './router';
import {i18n} from './i18n';

import resetStore from "@/stores/reset-store";
import '@mdi/font/css/materialdesignicons.css';
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate';

import {InferSeoMetaPlugin} from '@unhead/addons';

// Vuetify
import 'vuetify/styles';
import {createVuetify} from 'vuetify';
import * as components from 'vuetify/components';
import * as directives from 'vuetify/directives';
import {VDateInput} from 'vuetify/labs/VDateInput';
import {VIconBtn} from 'vuetify/labs/VIconBtn';
import {createRulesPlugin} from 'vuetify/labs/rules';
import {createVueI18nAdapter} from "vuetify/locale/adapters/vue-i18n";
import {useI18n} from "vue-i18n";
import {createHead} from "@unhead/vue/client";
import {VStepperVertical, VStepperVerticalItem} from 'vuetify/labs/VStepperVertical'

import {registerSW} from 'virtual:pwa-register'

const vuetify = createVuetify({
  components: {...components, VDateInput, VIconBtn, VStepperVertical, VStepperVerticalItem},
  directives,
  icons: {
    defaultSet: 'mdi' // This is already the default value - only for display purposes
  },
  locale: {
    adapter: createVueI18nAdapter({i18n, useI18n})
  }
});

const app = createApp(App);

const pinia = createPinia();
pinia.use(resetStore);
pinia.use(piniaPluginPersistedstate);

pinia.use(({store}) => {
  store.router = markRaw(router);
});

app.use(pinia);
if(!import.meta.env.DEV){
  const isProd = import.meta.env.MODE === 'prod';
  const sentryIntegrations = [Sentry.browserTracingIntegration({router}),
    Sentry.replayIntegration(),
    Sentry.browserProfilingIntegration(),
    Sentry.vueIntegration({
      tracingOptions: {
        trackComponents: true
      }
    })
  ];
  if(!isProd){
    sentryIntegrations.push(Sentry.feedbackIntegration({
      // Additional SDK configuration goes in here, https://docs.sentry.io/platforms/javascript/user-feedback/configuration/
      showBranding: false,
      isEmailRequired: true
    }));
  }
  Sentry.init({
    app,
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_SENTRY_PROJECT_NAME + "@" + __APP_VERSION__,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    integrations: sentryIntegrations,
    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    sendDefaultPii: true,
    // Performance Monitoring
    tracesSampleRate: .01, // Capture 1% of the transactions
    // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ["localhost", /^https:\/\/disita-dev\.witnet\.it\/api/, /^https:\/\/acasaapi-prod\.azurewebsites\.net\/api/], //TODO review
    // Session Replay
    replaysSessionSampleRate: .0005, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 0.91, // If you're not already sampling the entire session, change the sample rate to 91% when sampling sessions where errors occur.
    beforeSend(event, hint){ //https://docs.sentry.io/platforms/javascript/guides/vue/user-feedback/#integration
      // Check if it is an exception, and if so, show the report dialog
      if(!isProd && event.exception){
        Sentry.showReportDialog({eventId: event.event_id});
      }
      return event;
    }
  });
  pinia.use(createSentryPiniaPlugin());
}

app.config.globalProperties.$filters = {
  formatCurrency(number){
    return Intl.NumberFormat(undefined, {
      style: "currency",
      currency: "EUR",
      useGrouping: "always"
    }).format(number);
  },
  formatDate(date, showTime = false){
    const options = {};
    if(showTime){
      options.hour = "numeric";
      options.minute = "numeric";
      options.year = "numeric";
      options.month = "numeric";
      options.day = "numeric";
      options.hour12 = false;
    }
    return Intl.DateTimeFormat(undefined, options).format(new Date(date));
  }
};

const head = createHead({
  plugins: [InferSeoMetaPlugin()]
});
app.use(head);

app.use(i18n);
app.use(router);
app.use(vuetify);

const {t} = vuetify.locale;
const TAX_CODE_LENGTH = Number(import.meta.env.VITE_TAX_CODE_LENGTH);
const VAT_CODE_LENGTH = Number(import.meta.env.VITE_VAT_CODE_LENGTH);
const SDI_CODE_LENGTH = Number(import.meta.env.VITE_SDI_CODE_LENGTH);
const ZIP_CODE_LENGTH = Number(import.meta.env.VITE_ZIP_CODE_LENGTH);
app.use(createRulesPlugin({
  aliases: {
    taxCode: err => {
      return v => !v || v?.length === TAX_CODE_LENGTH || err || t('global.$vuetify.rules.tax_code_invalid')
    },
    taxOrVatCode: err => {
      return v => !v || v?.length === TAX_CODE_LENGTH || v?.length === VAT_CODE_LENGTH || err || t('global.$vuetify.rules.tax_code_invalid')
    },
    vatCode: err => {
      return v => !v || v?.length === VAT_CODE_LENGTH && /^\d+$/.test(v) || err || t('global.$vuetify.rules.vat_code_invalid')
    },
    sdiCode: err => {
      return v => !v || v?.length === SDI_CODE_LENGTH || err || t('global.$vuetify.rules.sdi_code_invalid')
    },
    zip: err => {
      return v => !v || v?.length === ZIP_CODE_LENGTH || err || t('global.$vuetify.rules.zip_code_invalid')
    },
    positive: err => {
      return v => v && v >= 0 || err || t('global.$vuetify.rules.positive_number')
    },
    isYear: err => {
      return v => v && v >= Number(import.meta.env.VITE_ORDER_MIN_YEAR) || err || t('global.$vuetify.rules.invalid_year')
    }
  }
}, vuetify.locale));

// Registra il service worker PWA
registerSW({
  onNeedRefresh(){
    // Mostra un prompt per aggiornare l'app
    console.log('Nuova versione disponibile');
  },
  onOfflineReady(){
    console.log('App pronta per l\'uso offline');
  },
});

// Inizializza Firebase Messaging dopo che l'app è montata
app.mount('#disita');
